/**
 * Single source of truth for CVE color + format logic, mirroring
 * threat-actor-format.ts's small/pure/defensively-typed helper convention.
 */
import type { CveReference } from '../../types';

// Re-exported, not duplicated — threat-actor-format.ts already owns hostname
// stripping for a reference URL, and two copies would only drift apart.
import { referenceDomain } from './threat-actor-format';
export { referenceDomain };

export function severityColor(severity?: string): string {
  if (!severity) return 'gray';
  if (severity.startsWith('Critical')) return 'red';
  if (severity.startsWith('High')) return 'orange';
  if (severity.startsWith('Medium')) return 'yellow';
  return 'gray';
}

export function formatEpss(epss?: number): string {
  if (epss === undefined || epss === null) return '';
  return `${(epss * 100).toFixed(1)}%`;
}

/** English ordinal suffix for a whole number (1 -> "st", 2 -> "nd", 11 -> "th", 21 -> "st"). */
export function ordinalSuffix(n: number): string {
  const abs = Math.abs(n) % 100;
  if (abs >= 11 && abs <= 13) return 'th';
  switch (abs % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export function formatReferenceLabel(ref: CveReference): string {
  return ref.label && ref.label.trim() ? ref.label : referenceDomain(ref.url);
}

/** CVSS attack-vector enums arrive upper-cased and sometimes underscored
 * ("LOCAL", "NETWORK", "ADJACENT_NETWORK"); render each word title-cased and
 * space-separated ("Local", "Network", "Adjacent Network") as a UI label. */
export function formatAttackVector(vector: string): string {
  if (!vector) return '';
  return vector
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface TimelineEntry {
  /** Formatted date (empty when the source line had no leading YYYY-MM-DD). */
  date: string;
  label: string;
}

/**
 * The ZDI disclosureTimeline arrives as a single stringified list, e.g.
 * `['2025-01-30 - Vulnerability reported to vendor', '2025-03-18 - ...']`.
 * Split it into entries on the `', '` item boundary (NOT on every quote, so
 * apostrophes inside a label like "doesn't warn" survive), strip the enclosing
 * brackets/quotes, and peel a leading `YYYY-MM-DD -` date off each label so the
 * panel can render a real timeline instead of a raw array string. Falls back to
 * newline/semicolon splitting for unquoted input; returns [] for empty input.
 */
export function parseDisclosureTimeline(raw?: string): TimelineEntry[] {
  if (!raw || !raw.trim()) return [];
  let body = raw.trim();
  if (body.startsWith('[') && body.endsWith(']')) {
    body = body.slice(1, -1).trim();
  }
  // Only split on a quote-comma-quote boundary between list items; a bare
  // apostrophe inside a label has no adjacent comma so it never splits.
  const hasItemBoundary = /["']\s*,\s*["']/.test(body);
  const parts = hasItemBoundary
    ? body.split(/["']\s*,\s*["']/)
    : body.split(/[\n;]+/);
  const items = parts
    .map((p) => p.trim().replace(/^["']/, '').replace(/["']$/, '').trim())
    .filter(Boolean);
  const entries: TimelineEntry[] = [];
  for (const item of items) {
    const match = /^(\d{4}-\d{2}-\d{2})\s*[-–—]\s*(.*)$/.exec(item);
    if (match) {
      entries.push({ date: formatCveDate(match[1]), label: match[2].trim() });
    } else {
      entries.push({ date: '', label: item });
    }
  }
  return entries;
}

export interface CweMitigation {
  /** SDLC phase the mitigation applies to ("Implementation"), '' when absent. */
  phase: string;
  /** Mitigation strategy ("Input Validation"), '' when absent. */
  strategy: string;
  label: string;
}

// The keys MITRE emits inside a potentialMitigations record. Order matters:
// "EFFECTIVENESS NOTES" must be tried before "EFFECTIVENESS" or the longer key
// would only ever match its prefix.
const CWE_MITIGATION_FIELD_RE =
  /(PHASE|STRATEGY|DESCRIPTION|EFFECTIVENESS NOTES|EFFECTIVENESS):/g;

/**
 * A CWE's `potentialMitigations` arrives as MITRE's flat export encoding —
 * `::` between records, `KEY:value` pairs within one, e.g.
 * `::PHASE:Implementation:STRATEGY:Input Validation:DESCRIPTION:Assume all…::`.
 * Values contain colons of their own, so fields are sliced between the known
 * key positions rather than split on `:`. Records with no DESCRIPTION carry no
 * guidance and are dropped; returns [] for empty input.
 */
export function parseCweMitigations(raw?: string): CweMitigation[] {
  if (!raw || !raw.trim()) return [];
  const entries: CweMitigation[] = [];
  for (const record of raw.split('::')) {
    if (!record.trim()) continue;
    // Array.from rather than spread — the app's tsconfig target predates
    // downlevelIteration, so a RegExpStringIterator can't be spread.
    const matches = Array.from(record.matchAll(CWE_MITIGATION_FIELD_RE));
    const fields: Record<string, string> = {};
    matches.forEach((match, i) => {
      const start = (match.index ?? 0) + match[0].length;
      const end = matches[i + 1]?.index ?? record.length;
      // Strip the separator colon that precedes the next key.
      fields[match[1]] = record.slice(start, end).replace(/:\s*$/, '').trim();
    });
    const label = fields.DESCRIPTION ?? '';
    if (!label) continue;
    entries.push({
      phase: fields.PHASE ?? '',
      strategy: fields.STRATEGY ?? '',
      label,
    });
  }
  return entries;
}

export function formatCveDate(date?: string): string {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  // timeZone: 'UTC' avoids the off-by-one-day shift that toLocaleDateString
  // would introduce for a bare YYYY-MM-DD (parsed as UTC midnight) in any
  // timezone behind UTC — see formatActorDate's identical UTC pin.
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}
